import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  mergeStyleSets,
  Pivot,
  PivotItem,
  Dropdown,
  IDropdownOption,
} from "@fluentui/react";
import {
  CaretLeft,
  TrendUp,
  Briefcase,
  Buildings,
  Users,
  Footprints,
  ShoppingCart,
  Airplane,
  HardHat,
  ChartLine,
  MapPin,
  ClockCountdown,
} from "@phosphor-icons/react";
import IconBadge from "../components/common/IconBadge";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { boroondaraPalette } from "../theme/boroondaraTheme";
import { accessService } from "../services/accessService";
import { useNavigate } from "react-router-dom";

// ── Constants ────────────────────────────────────────────
const COLORS = ["#2e873d","#38BDF8","#F59E0B","#F472B6","#A78BFA","#34D399","#FB923C","#06B6D4","#E11D48","#8B5CF6","#10B981","#6366F1"];
const tooltipStyle = { backgroundColor: "#fff", border: "1px solid #E1E1E1", borderRadius: 4, color: "#232323", fontSize: 12 };

// ── Helpers ──────────────────────────────────────────────
function fmtNum(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : n.toLocaleString("en-AU");
}
function fmtCurrency(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  if (isNaN(n)) return "-";
  if (Math.abs(n) >= 1_000_000_000) return "$" + (n / 1_000_000_000).toFixed(1) + "B";
  if (Math.abs(n) >= 1_000_000) return "$" + (n / 1_000_000).toFixed(1) + "M";
  if (Math.abs(n) >= 1_000) return "$" + (n / 1_000).toFixed(0) + "K";
  return "$" + n.toLocaleString("en-AU");
}
function fmtPct(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : n.toFixed(1) + "%";
}
function fmtAxis(value: number): string {
  if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
  if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(0) + "K";
  return String(value);
}

// ── Styles ───────────────────────────────────────────────
const p = boroondaraPalette;
const styles = mergeStyleSets({
  page: { padding: 32, minHeight: "calc(100vh - 52px)", background: "#F5F5F5", "@media (max-width: 768px)": { padding: 16 } } as any,
  container: { maxWidth: 1280, margin: "0 auto" },
  headerRow: { marginBottom: 24 },
  title: { color: p.text, fontWeight: 800 as any, fontSize: 28, letterSpacing: "-0.02em" },
  subtitle: { color: p.text2, marginTop: 6, maxWidth: 780, lineHeight: "1.6", fontSize: 14 },
  chipRow: { display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" } as any,
  chip: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: "#fff", border: "1px solid #E1E1E1", borderRadius: 16,
    padding: "4px 12px", fontSize: 12, color: p.text2,
  },
  filterBar: {
    display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap",
    background: "#fff", borderRadius: 10, padding: "16px 20px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", marginBottom: 24,
  } as any,
  filterDropdown: { minWidth: 160 } as any,
  filterReset: {
    background: "none", border: "1px solid #E1E1E1", borderRadius: 6,
    padding: "6px 14px", fontSize: 12, fontWeight: 600, color: p.text2,
    cursor: "pointer", height: 32, fontFamily: "inherit",
  },
  kpiGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 16, marginBottom: 28,
    "@media (max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)" },
  } as any,
  kpiCard: {
    background: "#fff", borderRadius: 10, padding: "20px 18px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: 4,
  } as any,
  kpiCardSoon: {
    background: "#fff", borderRadius: 10, padding: "20px 18px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: 4,
    opacity: 0.55,
  } as any,
  kpiLabel: { fontSize: 12, color: p.text2, fontWeight: 600 },
  kpiValue: { fontSize: 24, fontWeight: 800 as any, color: p.text },
  kpiSub: { fontSize: 11, color: p.text3 },
  chartsRow: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28,
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  } as any,
  chartCard: {
    background: "#fff", borderRadius: 10, padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  chartTitle: { fontSize: 14, fontWeight: 700, color: p.text, marginBottom: 12 },
  pivotWrap: { marginBottom: 28 },
  comingSoon: {
    background: "#fff", borderRadius: 12, padding: "48px 32px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", textAlign: "center",
    marginBottom: 28,
  } as any,
  comingSoonIcon: { marginBottom: 16 },
  comingSoonTitle: { fontSize: 18, fontWeight: 700, color: p.text, marginBottom: 8 },
  comingSoonDesc: { fontSize: 13, color: p.text2, lineHeight: "1.6", maxWidth: 520, margin: "0 auto" },
  comingSoonSource: {
    marginTop: 16, padding: "10px 16px", background: "#F9FAFB", borderRadius: 8,
    display: "inline-block", fontSize: 12, color: p.text3,
  },
  error: { marginBottom: 20 },
});

// ── Coming Soon component ────────────────────────────────
function ComingSoon({ title, description, source }: { title: string; description: string; source: string }) {
  return (
    <div className={styles.comingSoon}>
      <div className={styles.comingSoonIcon}>
        <ClockCountdown size={48} weight="duotone" color="#CBD5E1" />
      </div>
      <div className={styles.comingSoonTitle}>{title}</div>
      <div className={styles.comingSoonDesc}>{description}</div>
      <div className={styles.comingSoonSource}>Data source: {source}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════════
export default function EconomicDashboard() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kpis, setKpis] = useState<any>({});
  const [pedestrian, setPedestrian] = useState<any[]>([]);
  const [employment, setEmployment] = useState<any>({ salm: [], abs_employment: [] });
  const [business, setBusiness] = useState<any>({ summary: [], by_type: [], trend: [] });
  const [property, setProperty] = useState<any>({ valuations: [], permits: [], planning: [] });
  const [retail, setRetail] = useState<any[]>([]);
  const [tourism, setTourism] = useState<any[]>([]);
  const [infrastructure, setInfrastructure] = useState<any>({ capital_works: [], traffic: [] });

  // Filter state
  const [filterOptions, setFilterOptions] = useState<any>({ years: [], financial_years: [], sa2_areas: [], quarters: [] });
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [selectedFy, setSelectedFy] = useState<string>("");
  const [selectedSa2, setSelectedSa2] = useState<string>("");

  // Load filter options once
  useEffect(() => {
    accessService.get("/economic/filters").then((res: any) => setFilterOptions(res.data)).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const yearParam = selectedYear ? `year=${selectedYear}` : "";
      const sa2Param = selectedSa2 ? `sa2=${encodeURIComponent(selectedSa2)}` : "";
      const fyParam = selectedFy ? `fy=${encodeURIComponent(selectedFy)}` : "";
      const empQs = [yearParam, sa2Param].filter(Boolean).join("&");
      const propQs = [fyParam, sa2Param].filter(Boolean).join("&");
      const infraQs = [fyParam, sa2Param].filter(Boolean).join("&");

      const [kpiRes, pedRes, empRes, bizRes, propRes, retRes, tourRes, infraRes] = await Promise.all([
        accessService.get("/economic/kpis"),
        accessService.get("/economic/pedestrian?period=monthly").catch(() => ({ data: [] })),
        accessService.get(`/economic/employment${empQs ? "?" + empQs : ""}`).catch(() => ({ data: { salm: [], abs_employment: [] } })),
        accessService.get("/economic/business").catch(() => ({ data: { summary: [], by_type: [], trend: [] } })),
        accessService.get(`/economic/property${propQs ? "?" + propQs : ""}`).catch(() => ({ data: { valuations: [], permits: [], planning: [] } })),
        accessService.get("/economic/retail").catch(() => ({ data: [] })),
        accessService.get("/economic/tourism").catch(() => ({ data: [] })),
        accessService.get(`/economic/infrastructure${infraQs ? "?" + infraQs : ""}`).catch(() => ({ data: { capital_works: [], traffic: [] } })),
      ]);
      setKpis(kpiRes.data);
      setPedestrian(pedRes.data);
      setEmployment(empRes.data);
      setBusiness(bizRes.data);
      setProperty(propRes.data);
      setRetail(retRes.data);
      setTourism(tourRes.data);
      setInfrastructure(infraRes.data);
    } catch (err: any) {
      setError(err.message || "Failed to load economic data");
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedFy, selectedSa2]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Derived data helpers ──

  const hasData = (arr: any[] | undefined) => Array.isArray(arr) && arr.length > 0;

  // Planning by status (latest FY) — real data from planning_applications
  const planningByStatus = useMemo(() => {
    const plans = property.planning || [];
    if (plans.length === 0) return [];
    const latestFy = plans.reduce((max: string, r: any) => r.financial_year > max ? r.financial_year : max, "");
    const byStatus: Record<string, number> = {};
    plans.filter((r: any) => r.financial_year === latestFy).forEach((r: any) => {
      const s = r.status || "Unknown";
      byStatus[s] = (byStatus[s] || 0) + (Number(r.application_count) || 0);
    });
    return Object.entries(byStatus).map(([name, value]) => ({ name, value }));
  }, [property]);

  // Capital works by category — real data
  const cwByCategory = useMemo(() => {
    const cw = infrastructure.capital_works || [];
    if (cw.length === 0) return [];
    const latestFy = cw.reduce((max: string, r: any) => r.financial_year > max ? r.financial_year : max, "");
    return cw
      .filter((r: any) => r.financial_year === latestFy)
      .map((r: any) => ({
        category: r.category,
        budget: Number(r.total_budget) || 0,
        spend: Number(r.total_spend) || 0,
      }))
      .sort((a: any, b: any) => b.budget - a.budget);
  }, [infrastructure]);

  // Property valuations trend — real data
  const valTrend = useMemo(() => {
    const vals = property.valuations || [];
    if (vals.length === 0) return [];
    const byYear: Record<number, { year: number; residential: number; commercial: number }> = {};
    vals.forEach((r: any) => {
      const yr = r.valuation_year;
      if (!byYear[yr]) byYear[yr] = { year: yr, residential: 0, commercial: 0 };
      byYear[yr].residential += Number(r.residential_properties) || 0;
      byYear[yr].commercial += Number(r.commercial_properties) || 0;
    });
    return Object.values(byYear).sort((a, b) => a.year - b.year);
  }, [property]);

  // Traffic counts — real data
  const trafficData = useMemo(() => {
    return (infrastructure.traffic || [])
      .map((r: any) => ({ name: r.location_name, aadt: Number(r.aadt) || 0 }))
      .sort((a: any, b: any) => b.aadt - a.aadt)
      .slice(0, 12);
  }, [infrastructure]);

  // SALM trend — use LGA-level rows if available, otherwise average SA2s per quarter
  const salmTrend = useMemo(() => {
    const lgaRows = employment.salm.filter((r: any) => !r.sa2_code || r.sa2_code === "");
    if (lgaRows.length > 0) {
      return lgaRows.map((r: any) => ({
        period: `${r.quarter} ${r.year}`,
        rate: Number(r.smoothed_unemployment_rate) || 0,
      }));
    }
    // Compute LGA average from SA2-level data
    const byPeriod: Record<string, { sum: number; count: number }> = {};
    employment.salm.forEach((r: any) => {
      const key = `${r.quarter} ${r.year}`;
      if (!byPeriod[key]) byPeriod[key] = { sum: 0, count: 0 };
      byPeriod[key].sum += Number(r.smoothed_unemployment_rate) || 0;
      byPeriod[key].count++;
    });
    return Object.entries(byPeriod)
      .map(([period, v]) => ({ period, rate: Math.round((v.sum / v.count) * 10) / 10 }))
      .sort((a, b) => a.period.localeCompare(b.period));
  }, [employment]);

  // SALM by SA2
  const salmBySa2 = useMemo(() => {
    const sa2rows = employment.salm.filter((r: any) => r.sa2_code && r.sa2_code !== "");
    if (sa2rows.length === 0) return [];
    const latestYear = Math.max(...sa2rows.map((r: any) => r.year));
    const latestQuarter = sa2rows.filter((r: any) => r.year === latestYear).sort((a: any, b: any) => b.quarter.localeCompare(a.quarter))[0]?.quarter;
    return sa2rows
      .filter((r: any) => r.year === latestYear && r.quarter === latestQuarter)
      .map((r: any) => ({ name: r.sa2_name, rate: Number(r.smoothed_unemployment_rate) || 0 }))
      .sort((a: any, b: any) => b.rate - a.rate);
  }, [employment]);

  // Pedestrian trend
  const pedTrend = useMemo(() => {
    return pedestrian.map((r: any) => ({
      period: (r.period || "").slice(0, 7),
      count: Number(r.total_count) || 0,
    }));
  }, [pedestrian]);

  // Retail trend
  const retailTrend = useMemo(() => {
    const totals = retail.filter((r: any) => (r.industry_group || "").includes("Total"));
    return totals.map((r: any) => ({
      period: `${r.year}-${String(r.month).padStart(2, "0")}`,
      turnover: Number(r.turnover_million) || 0,
    }));
  }, [retail]);

  // Business by postcode
  const bizByPostcode = useMemo(() => {
    return (business.summary || []).map((r: any) => ({
      name: `${r.suburb || r.postcode} (${r.postcode})`,
      active: Number(r.active) || 0,
      inactive: Number(r.inactive) || 0,
    }));
  }, [business]);

  // Tourism by type
  const tourismByType = useMemo(() => {
    const byType: Record<string, number> = {};
    tourism.forEach((r: any) => {
      const t = r.visitor_type || "Unknown";
      byType[t] = (byType[t] || 0) + (Number(r.visitors_000) || 0);
    });
    return Object.entries(byType).map(([name, value]) => ({ name, value: Math.round(value * 10) / 10 }));
  }, [tourism]);

  // ── KPI cards ──
  const kpiCards = useMemo(() => {
    const cards: { icon: any; color: string; label: string; value: string; sub: string; live: boolean }[] = [];

    // Real data KPIs
    cards.push({
      icon: Buildings, color: "#7C3AED", label: "Median Property Value",
      value: kpis.median_property_value != null ? fmtCurrency(kpis.median_property_value) : "-",
      sub: kpis.property_year ? `${kpis.property_year} valuation` : "", live: true,
    });
    cards.push({
      icon: ChartLine, color: "#059669", label: "Planning Applications",
      value: kpis.planning_applications != null ? fmtNum(kpis.planning_applications) : "-",
      sub: kpis.development_value ? fmtCurrency(kpis.development_value) + " value" : "", live: true,
    });

    // Live API KPIs — show value if available, Coming Soon if not
    cards.push({
      icon: Users, color: "#E11D48", label: "Unemployment Rate",
      value: kpis.unemployment_rate != null ? fmtPct(kpis.unemployment_rate) : "Coming Soon",
      sub: kpis.unemployment_period || "SALM quarterly data", live: kpis.unemployment_rate != null,
    });
    cards.push({
      icon: Briefcase, color: "#2e873d", label: "Active Businesses",
      value: kpis.active_businesses ? fmtNum(kpis.active_businesses) : "Coming Soon",
      sub: kpis.active_businesses ? "ABR registered" : "ABR integration pending", live: !!kpis.active_businesses,
    });
    cards.push({
      icon: Footprints, color: "#0EA5E9", label: "Pedestrian Volume",
      value: kpis.pedestrian_30d ? fmtNum(kpis.pedestrian_30d) : "Coming Soon",
      sub: kpis.pedestrian_30d ? `${kpis.sensor_count} sensors (30d)` : "Awaiting council sensor deployment", live: !!kpis.pedestrian_30d,
    });
    cards.push({
      icon: HardHat, color: "#D97706", label: "Building Permits",
      value: kpis.total_permits ? fmtNum(kpis.total_permits) : "Coming Soon",
      sub: kpis.total_permit_value ? fmtCurrency(kpis.total_permit_value) + " value" : "data.vic.gov.au integration pending", live: !!kpis.total_permits,
    });
    cards.push({
      icon: ShoppingCart, color: "#F59E0B", label: "Retail Turnover (VIC)",
      value: kpis.retail_turnover_million != null ? `$${(kpis.retail_turnover_million).toFixed(0)}M` : "Coming Soon",
      sub: kpis.retail_period ? `Victoria — ${kpis.retail_period}` : "Victoria state-level proxy", live: kpis.retail_turnover_million != null,
    });
    cards.push({
      icon: Airplane, color: "#8B5CF6", label: "Tourism (Melbourne East)",
      value: kpis.tourism_visitors_000 != null ? fmtNum(Math.round(kpis.tourism_visitors_000 * 1000)) : "Coming Soon",
      sub: kpis.tourism_period ? `Melbourne East region — ${kpis.tourism_period}` : "Regional-level data", live: kpis.tourism_visitors_000 != null,
    });

    return cards;
  }, [kpis]);

  // ══════════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════════
  if (loading) {
    return (
      <div className={styles.page}>
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 120 }}>
          <Spinner size={SpinnerSize.large} label="Loading economic data..." />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>

        {/* Breadcrumb */}
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => nav("/dashboards")}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 13, fontWeight: 600, color: "#2e873d", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <CaretLeft size={10} weight="bold" /> Dashboards
          </button>
        </div>

        {/* Header */}
        <div className={styles.headerRow}>
          <div className={styles.title}>Economic Activity</div>
          <div className={styles.subtitle}>
            Property development, planning activity, infrastructure investment and economic insights for the City of Boroondara.
            Additional data sources including pedestrian counters, business registrations and retail spend are being integrated.
          </div>
          <div className={styles.chipRow}>
            <span className={styles.chip}><TrendUp size={12} weight="regular" /> Economic Indicators</span>
            <span className={styles.chip}><Buildings size={12} weight="regular" /> Property & Development</span>
            <span className={styles.chip}><MapPin size={12} weight="regular" /> Boroondara LGA</span>
          </div>
        </div>

        {/* Filter Bar */}
        <div className={styles.filterBar}>
          <Dropdown
            label="Year"
            placeholder="All years"
            className={styles.filterDropdown}
            selectedKey={selectedYear || ""}
            options={[
              { key: "", text: "All years" } as IDropdownOption,
              ...filterOptions.years.map((y: number) => ({ key: String(y), text: String(y) })),
            ]}
            onChange={(_, opt) => setSelectedYear(String(opt?.key || ""))}
            styles={{ dropdown: { minWidth: 120 }, title: { fontSize: 13 }, label: { fontSize: 12, fontWeight: 600 } }}
          />
          <Dropdown
            label="Financial Year"
            placeholder="All FYs"
            className={styles.filterDropdown}
            selectedKey={selectedFy || ""}
            options={[
              { key: "", text: "All FYs" } as IDropdownOption,
              ...filterOptions.financial_years.map((fy: string) => ({ key: fy, text: fy })),
            ]}
            onChange={(_, opt) => setSelectedFy(String(opt?.key || ""))}
            styles={{ dropdown: { minWidth: 130 }, title: { fontSize: 13 }, label: { fontSize: 12, fontWeight: 600 } }}
          />
          <Dropdown
            label="SA2 Area"
            placeholder="All areas"
            className={styles.filterDropdown}
            selectedKey={selectedSa2 || ""}
            options={[
              { key: "", text: "All areas" } as IDropdownOption,
              ...filterOptions.sa2_areas.map((s: string) => ({ key: s, text: s })),
            ]}
            onChange={(_, opt) => setSelectedSa2(String(opt?.key || ""))}
            styles={{ dropdown: { minWidth: 180 }, title: { fontSize: 13 }, label: { fontSize: 12, fontWeight: 600 } }}
          />
          {(selectedYear || selectedFy || selectedSa2) && (
            <button
              className={styles.filterReset}
              onClick={() => { setSelectedYear(""); setSelectedFy(""); setSelectedSa2(""); }}
            >
              Clear filters
            </button>
          )}
        </div>

        {error && (
          <MessageBar messageBarType={MessageBarType.error} className={styles.error}>{error}</MessageBar>
        )}

        {/* KPI Cards */}
        <div className={styles.kpiGrid}>
          {kpiCards.map((kpi, i) => (
            <div key={i} className={kpi.live ? styles.kpiCard : styles.kpiCardSoon}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <IconBadge icon={kpi.icon} size="sm" color={kpi.live ? kpi.color : "#CBD5E1"} weight="fill" />
                <span className={styles.kpiLabel}>{kpi.label}</span>
              </div>
              <div className={styles.kpiValue} style={{ color: kpi.live ? kpi.color : "#CBD5E1", fontSize: kpi.live ? 24 : 16 }}>
                {kpi.value}
              </div>
              <div className={styles.kpiSub}>{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* Tabbed Sections */}
        <div className={styles.pivotWrap}>
          <Pivot styles={{ root: { marginBottom: 20 }, link: { fontSize: 13 }, linkIsSelected: { fontSize: 13 } }}>

            {/* ── Overview Tab ── (real data) */}
            <PivotItem headerText="Overview">
              <div className={styles.chartsRow}>
                {/* Capital Works by Category */}
                <div className={styles.chartCard}>
                  <div className={styles.chartTitle}>Capital Works — Budget vs Spend by Category</div>
                  {cwByCategory.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={cwByCategory} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                        <XAxis dataKey="category" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                        <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtCurrency(v)} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="budget" name="Budget" fill="#D97706" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="spend" name="Actual Spend" fill="#38BDF8" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>No capital works data</div>}
                </div>

                {/* Planning Application Status */}
                <div className={styles.chartCard}>
                  <div className={styles.chartTitle}>Planning Application Status (Latest FY)</div>
                  {planningByStatus.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie data={planningByStatus} dataKey="value" nameKey="name" cx="50%" cy="50%"
                          outerRadius={110} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine fontSize={11}>
                          {planningByStatus.map((_: any, i: number) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>No planning data</div>}
                </div>
              </div>

              <div className={styles.chartsRow}>
                {/* Property Count Trend */}
                <div className={styles.chartCard}>
                  <div className={styles.chartTitle}>Property Count Trend</div>
                  {valTrend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={valTrend} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                        <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                        <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="residential" name="Residential" fill="#2e873d" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="commercial" name="Commercial" fill="#7C3AED" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>No property valuation data</div>}
                </div>

                {/* Traffic Volumes */}
                <div className={styles.chartCard}>
                  <div className={styles.chartTitle}>Traffic Volumes (AADT) — Top Locations</div>
                  {trafficData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={trafficData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                        <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={130} />
                        <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                        <Bar dataKey="aadt" name="AADT" fill="#059669" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>No traffic data</div>}
                </div>
              </div>
            </PivotItem>

            {/* ── Employment Tab ── */}
            <PivotItem headerText="Employment">
              {/* ABS Census employment — real data */}
              {hasData(employment.abs_employment) && (
                <div className={styles.chartCard} style={{ marginBottom: 28 }}>
                  <div className={styles.chartTitle}>Employment Participation by SA2 (ABS Census)</div>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart
                      data={employment.abs_employment.map((r: any) => ({
                        name: r.sa2_name,
                        rate: Number(r.employment_rate || r.participation_rate || 0),
                      }))}
                      margin={{ top: 5, right: 10, left: 10, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-45} textAnchor="end" interval={0} />
                      <YAxis tick={{ fontSize: 11 }} unit="%" />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtPct(v)} />
                      <Bar dataKey="rate" name="Employment Rate %" fill="#2e873d" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* SALM data — live API, may or may not be loaded */}
              {hasData(salmTrend) ? (
                <div className={styles.chartsRow}>
                  <div className={styles.chartCard}>
                    <div className={styles.chartTitle}>Unemployment Rate Trend — Boroondara LGA (SALM)</div>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={salmTrend} margin={{ top: 5, right: 20, left: 10, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                        <XAxis dataKey="period" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0} />
                        <YAxis tick={{ fontSize: 11 }} unit="%" />
                        <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtPct(v)} />
                        <Line type="monotone" dataKey="rate" name="Unemployment %" stroke="#E11D48" strokeWidth={2} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  {hasData(salmBySa2) && (
                    <div className={styles.chartCard}>
                      <div className={styles.chartTitle}>Unemployment by SA2 (Latest Quarter)</div>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={salmBySa2} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                          <XAxis type="number" tick={{ fontSize: 11 }} unit="%" />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={120} />
                          <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtPct(v)} />
                          <Bar dataKey="rate" name="Unemployment %" fill="#38BDF8" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              ) : (
                <ComingSoon
                  title="Small Area Labour Markets (SALM)"
                  description="Quarterly unemployment rates and labour force data at SA2 level for Boroondara. This data will be loaded automatically from the Department of Employment and Workplace Relations."
                  source="DEWR — lmip.gov.au (quarterly Excel release)"
                />
              )}
            </PivotItem>

            {/* ── Business Tab ── */}
            <PivotItem headerText="Business">
              {hasData(bizByPostcode) ? (
                <>
                  <div className={styles.chartsRow}>
                    <div className={styles.chartCard}>
                      <div className={styles.chartTitle}>Businesses by Suburb</div>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={bizByPostcode} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                          <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-45} textAnchor="end" interval={0} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip contentStyle={tooltipStyle} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="active" name="Active" stackId="a" fill="#2e873d" />
                          <Bar dataKey="inactive" name="Inactive" stackId="a" fill="#CBD5E1" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className={styles.chartCard}>
                      <div className={styles.chartTitle}>Business Type Distribution</div>
                      <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                          <Pie data={business.by_type?.map((r: any) => ({ name: r.entity_type, value: Number(r.count) })) || []}
                            dataKey="value" nameKey="name" cx="50%" cy="50%"
                            outerRadius={110} label={({ name, percent }) => `${(name || "").slice(0, 15)} ${(percent * 100).toFixed(0)}%`}
                            labelLine fontSize={10}>
                            {(business.by_type || []).map((_: any, i: number) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={tooltipStyle} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>
              ) : (
                <ComingSoon
                  title="Business Registrations"
                  description="Active business registrations across Boroondara postcodes (3101-3127) including entity types, GST status, and registration trends. Requires an ABR API key to be configured."
                  source="Australian Business Register (ABR) — abr.business.gov.au"
                />
              )}
            </PivotItem>

            {/* ── Property & Development Tab ── (mostly real data) */}
            <PivotItem headerText="Property & Development">
              <div className={styles.chartsRow}>
                {/* Property valuations — real data */}
                <div className={styles.chartCard}>
                  <div className={styles.chartTitle}>Property Count by Year</div>
                  {valTrend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={valTrend} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                        <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                        <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="residential" name="Residential" fill="#2e873d" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="commercial" name="Commercial" fill="#7C3AED" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>No data</div>}
                </div>

                {/* Planning status — real data */}
                <div className={styles.chartCard}>
                  <div className={styles.chartTitle}>Planning Application Status (Latest FY)</div>
                  {planningByStatus.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie data={planningByStatus} dataKey="value" nameKey="name" cx="50%" cy="50%"
                          outerRadius={120} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine fontSize={11}>
                          {planningByStatus.map((_: any, i: number) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={tooltipStyle} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>No data</div>}
                </div>
              </div>

              {/* Building permits — live API */}
              {hasData(property.permits) ? (
                <div className={styles.chartCard} style={{ marginBottom: 28 }}>
                  <div className={styles.chartTitle}>Building Permits by Financial Year</div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={(() => {
                      const byFy: Record<string, { fy: string; count: number; value: number }> = {};
                      (property.permits || []).forEach((r: any) => {
                        const fy = r.financial_year || "Unknown";
                        if (!byFy[fy]) byFy[fy] = { fy, count: 0, value: 0 };
                        byFy[fy].count += Number(r.permit_count) || 0;
                        byFy[fy].value += Number(r.total_value) || 0;
                      });
                      return Object.values(byFy).sort((a, b) => a.fy.localeCompare(b.fy));
                    })()} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                      <XAxis dataKey="fy" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Bar dataKey="count" name="Permits" fill="#D97706" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <ComingSoon
                  title="Building Permits"
                  description="Building permit volumes, types and estimated construction values for Boroondara. Data will be loaded automatically from the Victorian Building Authority open data."
                  source="data.vic.gov.au — Victorian Building Authority"
                />
              )}
            </PivotItem>

            {/* ── Activity & Visitors Tab ── */}
            <PivotItem headerText="Activity & Visitors">
              {/* Pedestrian counts */}
              {hasData(pedTrend) ? (
                <div className={styles.chartCard} style={{ marginBottom: 28 }}>
                  <div className={styles.chartTitle}>Pedestrian Activity (Monthly)</div>
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={pedTrend} margin={{ top: 5, right: 20, left: 10, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                      <XAxis dataKey="period" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                      <Line type="monotone" dataKey="count" name="Total Count" stroke="#0EA5E9" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <ComingSoon
                  title="Pedestrian Counters"
                  description="Hourly pedestrian counts from sensors across Boroondara activity centres including Glenferrie Rd, Burke Rd Camberwell, and Kew Junction. This data will become available once the council deploys pedestrian counting sensors in its activity centres."
                  source="City of Boroondara — pedestrian sensor network (pending deployment)"
                />
              )}

              {/* Retail trade */}
              {hasData(retailTrend) ? (
                <div className={styles.chartCard} style={{ marginBottom: 28 }}>
                  <div className={styles.chartTitle}>Retail Turnover — Victoria State (Monthly, ABS)</div>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={retailTrend} margin={{ top: 5, right: 20, left: 10, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                      <XAxis dataKey="period" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={Math.max(Math.floor(retailTrend.length / 12), 0)} />
                      <YAxis tick={{ fontSize: 11 }} unit="M" />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `$${v.toFixed(1)}M`} />
                      <Line type="monotone" dataKey="turnover" name="Turnover ($M)" stroke="#F59E0B" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <ComingSoon
                  title="Retail Trade (Victoria)"
                  description="Monthly retail trade turnover for Victoria by industry group (food, household goods, clothing, cafes & restaurants). Note: this is state-level data used as a broader economic indicator — Boroondara-specific retail data is not publicly available."
                  source="ABS Retail Trade, Australia (cat. 8501.0) — Victoria state level"
                />
              )}

              {/* Tourism */}
              {hasData(tourismByType) ? (
                <div className={styles.chartCard} style={{ marginBottom: 28 }}>
                  <div className={styles.chartTitle}>Tourism Visitors — Melbourne East Region (includes Boroondara)</div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={tourismByType} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 11 }} unit="K" />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `${v}K visitors`} />
                      <Bar dataKey="value" name="Visitors (000s)" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <ComingSoon
                  title="Tourism Visitors (Melbourne East Region)"
                  description="Quarterly domestic and international visitor statistics for the Melbourne East tourism region. Note: this covers a broader region that includes Boroondara along with neighbouring LGAs — Boroondara-specific tourism data is not separately published."
                  source="Tourism Research Australia (TRA) — Melbourne East tourism region"
                />
              )}
            </PivotItem>

          </Pivot>
        </div>

        {/* ── Data Sources ── */}
        <div style={{
          background: "#fff", borderRadius: 10, padding: "24px 28px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)", marginBottom: 28,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: p.text, marginBottom: 12 }}>Data Sources</div>
          <div style={{ fontSize: 12, lineHeight: "2", color: p.text2 }}>
            <div><strong>Property Valuations & Planning Applications</strong> — City of Boroondara, by SA2 area</div>
            <div><strong>Capital Works & Traffic Counts</strong> — City of Boroondara, by SA2 area</div>
            <div><strong>Employment & Demographics</strong> — Australian Bureau of Statistics, Census 2021 (cat. 2001.0)</div>
            <div><strong>Unemployment Rates</strong> — Dept of Employment and Workplace Relations, Small Area Labour Markets (SALM) quarterly release, smoothed SA2 estimates (<a href="https://www.dewr.gov.au/employment-research/small-area-labour-markets" target="_blank" rel="noopener noreferrer" style={{ color: "#2e873d" }}>dewr.gov.au</a>)</div>
            <div><strong>Building Permits</strong> — Victorian Building Authority, DataVic Building Permits state-wide dataset, filtered to Boroondara municipality (<a href="https://www.vba.vic.gov.au/about/data" target="_blank" rel="noopener noreferrer" style={{ color: "#2e873d" }}>vba.vic.gov.au</a>)</div>
            <div><strong>Business Registrations</strong> — Australian Business Register, SearchByPostcode API for postcodes 3101-3127 (<a href="https://abr.business.gov.au" target="_blank" rel="noopener noreferrer" style={{ color: "#2e873d" }}>abr.business.gov.au</a>) — requires API key</div>
            <div style={{ marginTop: 8, fontSize: 11, color: p.text3 }}>
              All public data is sourced under Creative Commons licensing. Datasets marked "Coming Soon" are pending API integration or council sensor deployment.
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
